import { memo, useEffect, useMemo, useState } from 'react';
import { useModal } from '../../../hooks/useModal';
import { camerasApi, useUpdateCameraNameMutation } from '../api/camerasApi';
import { Camera } from '../api/types';
import { Input } from '../../../components/Input';
import { PrimaryStyle } from '../../../components/Button';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../../store';

type UpdateCameraNameProps = {
  camera: Camera | null;
  setEditCamera: (camera: Camera | null) => void;
};

export const useUpdateCameraName = ({ camera, setEditCamera }: UpdateCameraNameProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => {
    setIsOpen(false);
    setEditCamera(null);
  };
  const { ModalComponent } = useModal({
    isOpen,
    onClose,
    title: 'Update camera name'
  });

  useEffect(() => {
    if (camera) {
      setIsOpen(true);
    }
  }, [camera]);

  const NewNameModal = () => (
    <ModalComponent>
      <EditForm camera={camera} onClose={onClose} />
    </ModalComponent>
  );
  return { NewNameModal: useMemo(() => memo(NewNameModal), [NewNameModal]) };
};

type EditFormProps = {
  camera: Camera | null;
  onClose: () => void;
};

const EditForm = ({ camera, onClose }: EditFormProps) => {
  const dispatch = useAppDispatch();
  const [newName, setNewName] = useState('');
  const [updateCameraName, { isLoading: loadingNewName }] = useUpdateCameraNameMutation();

  const isEditDisabled = !newName || newName === camera?.nicknameInDatabase || loadingNewName;

  // Treat the backend's default value as "no nickname set" so we start
  // the form blank with a helpful placeholder instead of forcing the
  // customer to delete the placeholder text first.
  const isDefaultNickname = (camera?.nicknameInDatabase ?? '')
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '') === 'set a nickname';

  useEffect(() => {
    if (camera) {
      setNewName(isDefaultNickname ? '' : camera.nicknameInDatabase);
    } else {
      setNewName('');
    }
  }, [camera, isDefaultNickname]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (camera) {
      try {
        await updateCameraName({ iccid: camera.SIM_ICCID, name: newName }).unwrap();
        onClose();
        //update cached data, so we don't need to fetch it again
        dispatch(
          camerasApi.util.updateQueryData('getCameras', undefined, (data) => {
            return {
              ...data,
              cameras: data.cameras.map((c) =>
                c.SIM_ICCID === camera.SIM_ICCID ? { ...c, nicknameInDatabase: newName } : c
              )
            };
          })
        );
        toast.success('Camera name updated successfully');
      } catch (e) {
        console.log(e);
        toast.error('Failed to update camera name');
      }
    }
  };

  return (
    <StyledForm onSubmit={onSubmit}>
      <Input
        label="Name"
        value={newName}
        placeholder={isDefaultNickname ? 'Enter a nickname for this camera' : undefined}
        onChange={(e) => {
          e.preventDefault();
          setNewName(e.target.value);
        }}
      />
      <StyledSubmit
        type="submit"
        value={loadingNewName ? 'Updating...' : 'Update'}
        disabled={isEditDisabled}
      />
    </StyledForm>
  );
};

const StyledSubmit = styled.input<{ disabled?: boolean }>`
  ${PrimaryStyle}
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  justify-content: space-between;
`;
